import { useCallback, useMemo, useState } from 'react';
import { IFormContext } from '../Context';
import { OnSubmit } from '../FormProps';
import { FieldInstance } from '../../Field';
import { FieldErrorMap } from '../FieldErrorMap';
import { FormInstance, RequiredStrategy } from '../FormInstance';

export type UseFormProps<T extends Record<string, any>> = {
    onSubmit?: OnSubmit<T>;
    submitWhenInvalid?: boolean;
    requiredStrategy?: RequiredStrategy;
    initialValues?: Partial<T>;
};

export const useForm = <T extends Record<string, any>>(props: UseFormProps<T>): IFormContext<T> => {
    const { onSubmit, submitWhenInvalid = false, requiredStrategy = 'required' } = props;

    const [formFields, setFormFields] = useState<Array<FieldInstance<any>>>([]);
    const [_isDirty, _setIsDirty] = useState<boolean | null>(null);
    const [_isTouched, _setIsTouched] = useState<boolean | null>(null);
    const [_isValidating, _setIsValidating] = useState<boolean | null>(null);
    const initialValues = useMemo(() => {
        return props.initialValues || {};
    }, [props.initialValues]);

    const values = useMemo<Partial<T>>(() => {
        return formFields.reduce<Partial<T>>((formState, field) => {
            return { ...formState, ...{ [field.name]: field.value } };
        }, {});
    }, [formFields]);

    const errorMap = useMemo<FieldErrorMap<T>>(() => {
        return formFields.reduce<FieldErrorMap<T>>((partialErrorMap, field) => {
            if (field.errors.length > 0) {
                return { ...partialErrorMap, ...{ [field.name]: field.errors } };
            }

            return partialErrorMap;
        }, {});
    }, [formFields]);

    const errors = useMemo<string[]>(() => {
        return Object.values(errorMap).flatMap((fieldErrors) => {
            return fieldErrors ?? [];
        });
    }, [errorMap]);

    const isValid = useMemo<boolean>(() => {
        return Object.keys(errorMap).length > 0;
    }, [errorMap]);

    const reset = useCallback(() => {
        //
    }, []);

    const setErrors = useCallback(
        (errorMap: FieldErrorMap<T>) => {
            formFields.forEach((fieldInstance) => {
                const fieldErrors = errorMap[fieldInstance.name];

                if (fieldErrors) {
                    fieldInstance.setErrors(Object.values(fieldErrors));
                }
            });
        },
        [formFields]
    );

    const setIsDirty = useCallback(() => {
        _setIsDirty(true);
    }, []);

    const setIsTouched = useCallback(() => {
        _setIsTouched(true);
    }, []);

    const getFieldByName = useCallback(
        <Name extends keyof T>(name: Name): FieldInstance<T[Name]> | null => {
            return formFields.find((field) => field.name === name) ?? null;
        },
        [formFields]
    );

    const setField = useCallback((nextField: FieldInstance<any>) => {
        setFormFields((formFields) => {
            const registeredFields = [...formFields];
            const registeredField = formFields.find((field) => field.name === nextField.name);

            if (registeredField) {
                registeredFields.splice(formFields.indexOf(registeredField), 1);
            }

            return registeredFields.concat([nextField]);
        });
    }, []);

    const removeField = useCallback((field: FieldInstance<any>) => {
        setFormFields((formFields) => {
            return formFields.filter((formField) => formField.name !== field.name);
        });
    }, []);

    const validateOnSubmit = useCallback((): Promise<boolean[]> => {
        _setIsValidating(true);

        return Promise.all(
            formFields.map(async (fieldInstance) => {
                return fieldInstance.validateOnSubmit();
            })
        ).finally(() => {
            _setIsValidating(false);
        });
    }, [formFields]);

    const setFieldValue = useCallback(
        (name: string, value: any) => {
            const field = getFieldByName(name);
            if (field) {
                field.setValue(value);
            }
        },
        [getFieldByName]
    );

    const formInstance = useMemo<FormInstance<T>>(() => {
        return {
            setField: setField,
            removeField: removeField,
            setFieldValue: setFieldValue,
            setIsDirty: setIsDirty,
            setIsTouched: setIsTouched,
            setErrors: setErrors,
            reset: reset,
            getField: getFieldByName,

            errors: errors,
            errorMap,
            isDirty: _isDirty ?? false,
            isTouched: _isTouched ?? false,
            isValid: isValid,
            isValidating: _isValidating ?? false,
            values: values ?? {},
            requiredStrategy,
            initialValues,
        };
    }, [
        errors,
        errorMap,
        _isDirty,
        _isTouched,
        isValid,
        _isValidating,
        values,
        formFields,
        setErrors,
        setFieldValue,
        requiredStrategy,
        initialValues,
    ]);

    const submit = useCallback(async () => {
        const validationResults = await validateOnSubmit();
        if (!submitWhenInvalid) {
            if (validationResults.some((pass) => !pass)) {
                return;
            }
        }
        await onSubmit?.(values, formInstance);
    }, [values, formInstance, onSubmit]);

    const contextInstance = useMemo<IFormContext<T>>(() => {
        return {
            formFields,
            submit,
            ...formInstance,
        };
    }, [formInstance, formFields, submit]);

    return contextInstance;
};
